import { Agenda, Reservation, User } from '../types';
import { devLog } from '../utils/devLog';
import { parseJsonResponse } from '../utils/apiUtils';
import { getApiBaseUrl } from '../config/api';
import { AUTH_SEED } from '../config/api';
import { withRetry, tryMultipleEndpoints, isRetryableError } from '../utils/apiRetry';
import { montarValorAutorizacao, limparAutenticacao, obterTokenAutenticacaoCliente } from '../utils/authToken';
import ptJson from '../locales/pt.json';
import enJson from '../locales/en.json';
import esJson from '../locales/es.json';

interface ClienteResponse {
  status: string;
  message?: string;
  data?: User;
}

interface OrigemResponse {
  status: string;
  message?: string;
  data?: Array<{value: number, label: string}>;
}

interface EstadoResponse {
  status: string;
  message?: string;
  data?: Array<{value: number, label: string}>;
}

interface CidadeResponse {
  status: string;
  message?: string;
  data?: Array<{value: number, label: string}>;
}

interface PaisResponse {
  status: string;
  message?: string;
  data?: Array<{codigo_ibge: number, nome: string, sigla: string, prefixo_telefone: string}>;
}

interface TrocarSenhaResponse {
  status: string;
  message: string;
}

export class ReservationService {
  // Configuration seed for Basic Auth (imported from config)
  
  // {|INICIO| 13/07/2026 às 11:41 - PRIORIZAR BEARER DO CLIENTE NAS ROTAS CRITICAS DE RESERVA}
  private static getAuthHeaders(): Headers {
    const headers = new Headers();
    headers.append("Authorization", montarValorAutorizacao(AUTH_SEED));
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");
    return headers;
  }
  // {|FIM| 13/07/2026 às 11:41 - PRIORIZAR BEARER DO CLIENTE NAS ROTAS CRITICAS DE RESERVA}

  // {|INICIO| 22/07/2026 às 16:05 - DEBUG: SEPARAR CABECALHOS BEARER E BASIC PARA EVITAR 401 EM ROTAS PUBLICAS}
  /**
   * Function-level comment: Monta headers Basic-only para rotas públicas ou sem sessão obrigatória, evitando Bearer inválido herdado do navegador.
   */
  private static getBasicAuthHeaders(): Headers {
    const headers = new Headers();
    headers.append('Authorization', `Basic ${AUTH_SEED}`);
    headers.append('Content-Type', 'application/json');
    headers.append('Accept', 'application/json');
    return headers;
  }

  /**
   * Function-level comment: Monta headers Bearer-only para rotas que realmente dependem da sessão do cliente autenticado.
   */
  private static getBearerAuthHeaders(): Headers {
    const tokenAutenticacao = obterTokenAutenticacaoCliente();
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Accept', 'application/json');

    if (tokenAutenticacao) {
      headers.append('Authorization', `Bearer ${tokenAutenticacao}`);
    }

    return headers;
  }
  // {|FIM| 22/07/2026 às 16:05 - DEBUG: SEPARAR CABECALHOS BEARER E BASIC PARA EVITAR 401 EM ROTAS PUBLICAS}

  // {|INICIO| 15/06/2026 às 14:52 - NORMALIZAR FLAG DE PARCELAMENTO POR ITEM DA AGENDA}
  /**
   * Function-level comment: Converte flags S/N, 1/0 e booleanos em booleano para uso consistente no frontend.
   */
  private static normalizeBooleanFlag(valorFlag: unknown): boolean | undefined {
    if (typeof valorFlag === 'undefined' || valorFlag === null || valorFlag === '') {
      return undefined;
    }

    if (typeof valorFlag === 'boolean') {
      return valorFlag;
    }

    const valorNormalizado = String(valorFlag).trim().toUpperCase();
    return ['S', '1', 'T', 'TRUE'].includes(valorNormalizado);
  }
  // {|FIM| 15/06/2026 às 14:52 - NORMALIZAR FLAG DE PARCELAMENTO POR ITEM DA AGENDA}

  /**
   * Obtém a lista de países disponíveis
   * @returns Lista de países ou mensagem de erro
   */
  static async getPaises(): Promise<PaisResponse> {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${AUTH_SEED}`,
        'Content-Type': 'application/json'
      },
      redirect: 'follow' as RequestRedirect
    };

    try {
      // Constrói a URL da API para buscar os países
      const url = `${getApiBaseUrl()}/reserva_online/getPaises/`;
      
      // Faz a chamada à API
      const response = await fetch(url, requestOptions);
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar países: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // A API retorna uma estrutura aninhada: {status: "success", data: {status: "Sucesso", data: [...]}}
      // Os dados vêm com campos "value" e "label", precisamos mapear para o formato esperado
      if (data.status === 'success' && data.data && data.data.status === 'Sucesso') {
        const rawCountries = data.data.data || [];
        
        // Mapear os campos da API para o formato esperado pelo componente
        const mappedCountries = rawCountries.map((country: { value: number; label: string }) => ({
          codigo_ibge: country.value,
          nome: country.label,
          sigla: '', // A API não retorna sigla, vamos deixar vazio por enquanto
          prefixo_telefone: '' // A API não retorna prefixo, vamos deixar vazio por enquanto
        }));
        
        return { 
          status: 'Sucesso', 
          data: mappedCountries
        };
      }
      
      // Verifica se a resposta contém um erro
      if (data.status === 'Erro' || (data.data && data.data.status === 'Erro')) {
        return { status: 'Erro', message: data.message || data.data?.message };
      }
      
      // Fallback para estrutura antiga
      return { 
        status: 'Sucesso', 
        data: data.data || data
      };
    } catch (error) {
      devLog('Erro ao buscar países:', error);
      return { status: 'Erro', message: error instanceof Error ? error.message : this.getTranslation('errors.unknown') };
    }
  }

  /**
   * Obtém o histórico de vendas do cliente
   * @param idCliente ID do cliente
   * @returns Histórico de vendas
   */
  static async getHistoricoVendas(idCliente: number, mes?: string, ano?: string): Promise<any[]> {
    try {
      // Constrói a URL da API para buscar o histórico de vendas do cliente
      let url = `${getApiBaseUrl()}/reserva_online/onHistoricoVendas/?idcliente=${idCliente}`;
      
      if (mes) url += `&mes=${mes}`;
      if (ano) url += `&ano=${ano}`;
      
      const requestOptions = {
        method: 'GET',
        headers: ReservationService.getAuthHeaders(),
        redirect: 'follow' as RequestRedirect,
      };
      
      // Faz a chamada à API
      const response = await fetch(url, requestOptions);
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar histórico de vendas: ${response.statusText}`);
      }
      
      const data = await parseJsonResponse(response);
      
      // Verifica se a resposta contém um erro
      if (data.status === 'Erro') {
        throw new Error(`${data.message}`);
      }
      
      // Se a resposta tem status success, usa data.data, senão usa data diretamente
      let historicoData = data.status === 'success' ? data.data : data;
      
      // Verifica se houve duplo encapsulamento (comum em alguns endpoints legados)
      if (historicoData && historicoData.status === 'success' && Array.isArray(historicoData.data)) {
        historicoData = historicoData.data;
      }
      
      // Se não houver histórico, retorna um array vazio
      if (!Array.isArray(historicoData)) {
        return [];
      }
      
      return historicoData;
    } catch (error) {
      console.error('Erro ao buscar histórico de vendas', error);
      return [];
    }
  }

  /**
   * Obtém o resultado da corrida (PDF ou HTML)
   * @param file Caminho ou nome do arquivo
   */
  static async getRaceResult(file: string): Promise<{ base64: string, filename: string, mime: string }> {
    try {
        const url = /^https?:\/\//i.test(file)
          ? file
          : `${getApiBaseUrl()}/reserva_online/obterResultadoPista/?file=${encodeURIComponent(file)}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: ReservationService.getAuthHeaders()
        });
        
        if (!response.ok) {
            throw new Error(`Erro ao buscar resultado: ${response.statusText}`);
        }
        
        const rawData = await parseJsonResponse(response);
        
        // Verifica se houve duplo encapsulamento (comum em alguns endpoints legados)
        let data = rawData;
        // Verifica se data.data existe e é um objeto que contém status
        if (data.status === 'success' && data.data && typeof data.data === 'object' && 'status' in data.data) {
            data = data.data;
        }
        
        if (data.status === 'success' && data.data) {
            if (data.data.mode === 'base64') {
                return {
                    base64: data.data.content,
                    filename: data.data.filename,
                    mime: data.data.mime || 'application/pdf'
                };
            } else {
                 // Se o conteúdo não for o esperado (ex: texto de teste "teste" inserido no banco), tratamos como sem conteúdo
                 throw new Error('Conteúdo do resultado não disponível para visualização.');
            }
        }
        
        throw new Error(data.message || 'Erro desconhecido ao buscar resultado');
    } catch (error) {
        console.error('Erro ao buscar resultado da corrida', error);
        throw error;
    }
  }

  /**
   * Valida os dados do usuário antes de criar
   * @param userData Dados do usuário a serem validados
   * @returns Objeto com status e mensagem em caso de erro, ou null se válido
   */
  static validateUser(userData: Partial<User>): { status: string; message: string } | null {
    const isBrazil = !userData.codigo_ibge_pais || userData.codigo_ibge_pais === 1058;
    const docLabel = isBrazil ? this.getTranslation('auth.cpf') : this.getTranslation('auth.passport');

    // Validação de campos obrigatórios
    if (!userData.cpf_cnpj || !userData.cpf_cnpj.toString().trim()) {
        return { status: 'Erro', message: this.getTranslation('auth.documentRequired', { doc: docLabel }) };
    }

    const requiredFields: Record<string, string> = {
      nome: this.getTranslation('registration.fullName'),
      email: this.getTranslation('registration.email'),
      data_nascimento: this.getTranslation('registration.birthDate'),
      telefone_celular: this.getTranslation('registration.cellPhone')
    };
    
    for (const [field, label] of Object.entries(requiredFields)) {
      if (!userData[field as keyof User] || !userData[field as keyof User]?.toString().trim()) {
        return { status: 'Erro', message: this.getTranslation('errors.fieldRequired', { label }) };
      }
    }

    // Validação do nome completo
    const nome = userData.nome?.trim() ?? '';
    const partesNome = nome.split(/\s+/);
    
    // Verifica se há pelo menos duas palavras com mais de uma letra
    const nomesValidos = partesNome.filter(parte => parte.length > 1);
    
    // Evita abreviações do tipo "J." ou "M."
    for (const parte of partesNome) {
      if (/^[A-Z]\.$/i.test(parte)) {
        return { status: 'Erro', message: this.getTranslation('errors.nameNoAbbreviations') };
      }
    }
    
    if (nomesValidos.length < 2) {
      return { status: 'Erro', message: this.getTranslation('errors.fullNameRequired') };
    }

    // Validação do CPF ou Passaporte
    if (isBrazil) {
        const cpf = userData.cpf_cnpj?.replace(/\D/g, '') ?? '';
        if (!this.validarCPF(cpf)) {
            return { status: 'Erro', message: this.getTranslation('auth.invalidDocument', { doc: docLabel }) };
        }
    } else {
        if (userData.cpf_cnpj!.trim().length < 5) {
             return { status: 'Erro', message: this.getTranslation('auth.invalidDocument', { doc: docLabel }) };
        }
    }

    // Validação do telefone celular (incluindo números internacionais)
    const telefone = userData.telefone_celular ?? '';
    if (!telefone) {
      return { status: 'Erro', message: this.getTranslation('errors.phoneRequired') };
    }

    // {|INICIO| 13/03/2026 às 10:05 - TELEFONE: VALIDAR POR PAÍS (BR DDD+9; INTERNACIONAL E.164 8..15) PARA SUPORTAR NÚMEROS ESTRANGEIROS}
    const digitsTelefone = String(telefone).replace(/\D/g, '');
    if (!digitsTelefone) {
      return { status: 'Erro', message: this.getTranslation('errors.phoneFormatInvalid') };
    }

    if (isBrazil) {
      const local = digitsTelefone.replace(/^55/, '');
      if (!/^(\d{2})9\d{8}$/.test(local)) {
        return { status: 'Erro', message: this.getTranslation('errors.phoneFormatInvalid') };
      }
    } else {
      if (digitsTelefone.length < 8 || digitsTelefone.length > 15) {
        return { status: 'Erro', message: this.getTranslation('errors.phoneFormatInvalid') };
      }
    }
    // {|FIM| 13/03/2026 às 10:05 - TELEFONE: VALIDAR POR PAÍS (BR DDD+9; INTERNACIONAL E.164 8..15) PARA SUPORTAR NÚMEROS ESTRANGEIROS}

    return null; // Dados válidos
  }

  /**
   * Valida um CPF
   * @param cpf CPF a ser validado (apenas números)
   * @returns true se o CPF for válido, false caso contrário
   */
  static validarCPF(cpf: string): boolean {
    // Remove caracteres não numéricos
    cpf = cpf.replace(/\D/g, '');
    
    // Verifica se tem 11 dígitos
    if (cpf.length !== 11) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cpf)) return false;
    
    // Validação do primeiro dígito verificador
    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let resto = soma % 11;
    const dv1 = resto < 2 ? 0 : 11 - resto;
    
    if (parseInt(cpf.charAt(9)) !== dv1) return false;
    
    // Validação do segundo dígito verificador
    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpf.charAt(i)) * (11 - i);
    }
    resto = soma % 11;
    const dv2 = resto < 2 ? 0 : 11 - resto;
    
    if (parseInt(cpf.charAt(10)) !== dv2) return false;
    
    return true;
  }

  /**
   * Cria um novo usuário
   * @param userData Dados do usuário a ser criado
   * @returns Resposta da API com os dados do usuário criado ou mensagem de erro
   */
  static async createUser(userData: Partial<User>): Promise<{ status: string; message: string; user?: User }> {
    try {
      // Validação dos dados
      const validationError = this.validateUser(userData);
      if (validationError) {
        return validationError;
      }

      // Formata os dados antes de enviar
      const isBrazil = !userData.codigo_ibge_pais || userData.codigo_ibge_pais === 1058;
      
      const formattedData = {
        ...userData,
        cpf_cnpj: isBrazil 
          ? (userData.cpf_cnpj?.replace(/\D/g, '') ?? '') 
          : (userData.cpf_cnpj?.trim().toUpperCase() ?? ''),
        cpf: isBrazil 
          ? (userData.cpf_cnpj?.replace(/\D/g, '') ?? '') 
          : (userData.cpf_cnpj?.trim().toUpperCase() ?? ''),
        telefone_celular: userData.telefone_celular?.replace(/\D/g, '') ?? '',
        data_nascimento: this.formatDate(userData.data_nascimento ?? ''),
        menor_de_idade: this.verificaIdade(userData.data_nascimento ?? ''),
        codigo_ibge_pais: userData.codigo_ibge_pais || 1058,
        tipo_sanguineo: '0',
        sexo: userData.sexo || 'O' // Sexo biológico (M/F/O)
      };

      // Constrói a URL da API para criar um usuário
      const url = `${getApiBaseUrl()}/index.php?class=UserRestService&method=create`;
      
      // Faz a chamada à API
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formattedData)
      });
      
      if (!response.ok) {
        throw new Error(`Erro ao criar usuário: ${response.statusText}`);
      }
      
      const data = await parseJsonResponse(response);
      
      // Verifica se a resposta contém um erro
      if (data.status === 'Erro') {
        return { status: 'Erro', message: data.message };
      }
      
      // Se o usuário foi criado com sucesso, envia e-mail de confirmação
      if (data.id) {
        await this.enviarEmailConfirmacaoCadastro(data.id);
      }

      const docLabel = (formattedData.codigo_ibge_pais === 1058) ? this.getTranslation('auth.cpf') : this.getTranslation('auth.passport');

      return { 
        status: 'Sucesso', 
        message: this.getTranslation('auth.userRegisteredSuccess', { doc: docLabel, value: formattedData.cpf_cnpj }),
        user: data
      };
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      return { 
        status: 'Erro', 
        message: error instanceof Error ? error.message : this.getTranslation('errors.unknown')
      };
    }
  }

  /**
   * Formata uma data para o formato esperado pela API
   * @param date Data a ser formatada
   * @returns Data formatada
   */
  static formatDate(date: string): string {
    try {
      const dateObj = new Date(date);
      return `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear()}`;
    } catch {
      return date;
    }
  }

  // Formata para ISO (YYYY-MM-DD), aceito pelo PHP DateTime
  static formatDateISO(date: string): string {
    try {
      const str = (date ?? '').toString().trim();
      if (!str) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
        const [dd, mm, yyyy] = str.split('/');
        return `${yyyy}-${mm}-${dd}`;
      }
      const d = new Date(str);
      if (!isNaN(d.getTime())) {
        return d.toISOString().slice(0, 10);
      }
      return str;
    } catch {
      return date;
    }
  }

  /**
   * Verifica se a pessoa é menor de idade
   * @param birthDate Data de nascimento
   * @returns true se for menor de idade, false caso contrário
   */
  static verificaIdade(birthDate: string): boolean {
    try {
      const today = new Date();
      const birthDateParts = birthDate.split('/');
      
      // Se a data estiver no formato DD/MM/YYYY
      if (birthDateParts.length === 3) {
        const birthDateObj = new Date(
          parseInt(birthDateParts[2]), // Ano
          parseInt(birthDateParts[1]) - 1, // Mês (0-11)
          parseInt(birthDateParts[0]) // Dia
        );
        
        let age = today.getFullYear() - birthDateObj.getFullYear();
        const monthDiff = today.getMonth() - birthDateObj.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDateObj.getDate())) {
          age--;
        }
        
        return age < 18;
      } else {
        // Se a data estiver em outro formato (ISO)
        const birthDateObj = new Date(birthDate);
        let age = today.getFullYear() - birthDateObj.getFullYear();
        const monthDiff = today.getMonth() - birthDateObj.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDateObj.getDate())) {
          age--;
        }
        
        return age < 18;
      }
    } catch (e) {
      console.error('Erro ao verificar idade:', e);
      return false;
    }
  }

  /**
   * Envia e-mail de confirmação de cadastro
   * @param userId ID do usuário
   */
  static async enviarEmailConfirmacaoCadastro(userId: number): Promise<void> {
    try {
      const url = `${getApiBaseUrl()}/index.php?class=EmailService&method=sendConfirmation&userId=${userId}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Erro ao enviar e-mail de confirmação: ${response.statusText}`);
      }
      
      const data = await parseJsonResponse(response);
      
      if (data.status === 'Erro') {
        throw new Error(`Erro ao enviar e-mail: ${data.message}`);
      }
      
    } catch (error) {
      console.error('Erro ao enviar e-mail de confirmação:', error);
      // Não lança o erro para não interromper o fluxo principal
    }
  }

  static async getAvailableSlots(date: string): Promise<Agenda[]> {
    try {
      return await withRetry(async () => {
        const params = new URLSearchParams({
          horainicio: date,
          horatermino: date
        });

        const idcliente = typeof window !== 'undefined' ? (window.localStorage?.getItem('login_id') || '') : '';
        if (idcliente) {
          params.append('idcliente', idcliente);
        }

        // {|INICIO| 29/09/2026 às 10:47 - CORRIGIR FALLBACK CROSS-BANCO NA CONSULTA DE AGENDA (EVITAR DIVERGENCIA ENTRE BANCOS)}
        // Versao anterior (historico): usava fallback cruzado /kgvt + /kgv no mesmo array e podia retornar agenda de outro ambiente
        /*
        const endpoints = [
          `${getApiBaseUrl()}/reserva_online?${params.toString()}`,
          `/kgvt/reserva_online?${params.toString()}`,
          `/kgv/reserva_online?${params.toString()}`
        ];
        */
        const endpoints = [
          `${getApiBaseUrl()}/reserva_online?${params.toString()}`
        ];
        // {|FIM| 29/09/2026 às 10:47 - CORRIGIR FALLBACK CROSS-BANCO NA CONSULTA DE AGENDA (EVITAR DIVERGENCIA ENTRE BANCOS)}
        
        return await tryMultipleEndpoints(endpoints, async (endpoint) => {
          
          // {|INICIO| 13/07/2026 às 11:41 - USAR HEADER AUTENTICADO CENTRAL PARA ENDPOINT DE AGENDA}
          // {|INICIO| 23/07/2026 às 09:48 - AUTH: VOLTAR A PRIORIZAR BEARER NAS CONSULTAS DE AGENDA DO CLIENTE}
          const response = await fetch(endpoint, {
            method: 'GET',
            headers: ReservationService.getAuthHeaders(),
            redirect: 'follow'
          });
          // {|FIM| 23/07/2026 às 09:48 - AUTH: VOLTAR A PRIORIZAR BEARER NAS CONSULTAS DE AGENDA DO CLIENTE}
          // {|FIM| 13/07/2026 às 11:41 - USAR HEADER AUTENTICADO CENTRAL PARA ENDPOINT DE AGENDA}
          
          
          if (!response.ok) {
            // {|INICIO| 21/07/2026 às 23:20 - INSTRUMENTAR ERRO BRUTO DO ENDPOINT DE AGENDA PARA DEBUG DO HTTP 500}
            const corpoBrutoResposta = await response.text();
            const corpoNormalizadoResposta = corpoBrutoResposta.replace(/\s+/g, ' ').trim();
            const trechoRespostaErro = corpoNormalizadoResposta.slice(0, 280);
            // {|FIM| 21/07/2026 às 23:20 - INSTRUMENTAR ERRO BRUTO DO ENDPOINT DE AGENDA PARA DEBUG DO HTTP 500}

            // Trata erro 403 Forbidden: limpa autenticação e força logout
            if (response.status === 403) {
              limparAutenticacao(true);
              throw new Error(this.getTranslation('errors.sessionExpired'));
            }
            throw new Error(`HTTP error! status: ${response.status} | endpoint: ${endpoint} | body: ${trechoRespostaErro || 'vazio'}`);
          }
          
          const data = await parseJsonResponse(response);
          
          if (data.status === 'success' && Array.isArray(data.data)) {
            // Comentário (function-level): Normalização dos slots disponíveis
            // Inclui `min_vagas_bat_fechada` quando presente na API, priorizado no Modal.
            return data.data.map((slot: any) => ({
              idagenda: slot.idagenda,
              nomeitem: slot.nomeitem,
              valorliquido: parseFloat(slot.valorliquido) || parseFloat(slot.valor) || parseFloat(slot.preco) || 0,
              horainicio: slot.horainicio,
              horatermino: slot.horatermino,
              vagas: parseInt(slot.vagas) || 0,
              vagas_totais: parseInt(slot.vagas_totais) || 0,
              status: slot.status || 'A',
              fechado: slot.fechado === '1' || slot.fechado === 1,
              parcela_cartao_venda_online: this.normalizeBooleanFlag(slot.parcela_cartao_venda_online),
              parcelamento_venda_online_efetivo: this.normalizeBooleanFlag(slot.parcelamento_venda_online_efetivo),
              /* {|INICIO| 18/06/2026 às 16:39 - NORMALIZAR LIMITE EXCLUSIVO DE QUANTIDADE NA API DA AGENDA} */
              permite_somente_quantidade: (typeof slot.permite_somente_quantidade !== 'undefined')
                ? (typeof slot.permite_somente_quantidade === 'string'
                    ? parseInt(slot.permite_somente_quantidade)
                    : Number(slot.permite_somente_quantidade))
                : undefined,
              quantidade_maxima_por_cpf_disponivel: (typeof slot.quantidade_maxima_por_cpf_disponivel !== 'undefined')
                ? (typeof slot.quantidade_maxima_por_cpf_disponivel === 'string'
                    ? parseInt(slot.quantidade_maxima_por_cpf_disponivel)
                    : Number(slot.quantidade_maxima_por_cpf_disponivel))
                : undefined,
              /* {|FIM| 18/06/2026 às 16:39 - NORMALIZAR LIMITE EXCLUSIVO DE QUANTIDADE NA API DA AGENDA} */
              // Alguns backends retornam como string; fazer parse seguro
              min_vagas_bat_fechada: (typeof slot.min_vagas_bat_fechada !== 'undefined')
                ? (typeof slot.min_vagas_bat_fechada === 'string'
                    ? parseInt(slot.min_vagas_bat_fechada)
                    : Number(slot.min_vagas_bat_fechada))
                : undefined
            }));
          } else {
            throw new Error(data.message || this.getTranslation('errors.slotsFetchError'));
          }
        });
      }, {
        maxRetries: 2,
        delayMs: 1000,
        shouldRetry: (error, attempt) => {
          return attempt < 2 && isRetryableError(error);
        }
      });
    } catch (error) {
      console.error('Error fetching available slots:', error);
      // Se o erro já é sobre sessão expirada, re-lança sem alterar
      if (error instanceof Error && (
        error.message.includes('Sessão expirada') ||
        error.message.includes('Session expired') ||
        error.message.includes('token expirado') ||
        error.message.includes('Token expired')
      )) {
        throw error;
      }
      throw new Error(this.getTranslation('errors.invalidServerResponse'));
    }
  }

  static async createReservation(reservationData: {
    idagenda: number;
    idcliente: number;
    tipo_reserva: Reservation['tipo_reserva'];
    qtdpessoas: number;
    valor_unitario: number;
    valor_total: number;
    data_corrida?: string; // Adicionar data da corrida
  }): Promise<Reservation> {
    try {
      // {|INICIO| 04/08/2026 às 01:07 - REMOVER ENDPOINT LOCAL DE DEBUG (EVENT)}
      // {|FIM| 04/08/2026 às 01:07 - REMOVER ENDPOINT LOCAL DE DEBUG (EVENT)}
      // Tenta múltiplos endpoints com retry
      return await withRetry(async () => {
        // {|INICIO| 29/09/2026 às 10:45 - CORRIGIR FALLBACK CROSS-BANCO (CAUSA RAIZ RESERVA 111733/103968 DUPLICADA EM BANCOS DIFERENTES)}
        // Versao anterior (historico): usava fallback cruzado /kgvt + /kgv no mesmo array e causava duplicata duplicada em bancos separados):
        /*
        const endpoints = [
          `${getApiBaseUrl()}/reserva_online`, // Proxy local (desenvolvimento) - único endpoint funcional
          `/kgvt/reserva_online`,
          `/kgv/reserva_online`
        ];
        */
        const endpoints = [
          `${getApiBaseUrl()}/reserva_online`
        ];
        // {|FIM| 29/09/2026 às 10:45 - CORRIGIR FALLBACK CROSS-BANCO (CAUSA RAIZ RESERVA 111733/103968 DUPLICADA EM BANCOS DIFERENTES}
        
        return await tryMultipleEndpoints(endpoints, async (endpoint) => {
          const url = endpoint;
          
          // requestData removido: usamos FormData para compatibilidade com backend legado
          
          //tipo_reserva: (item.tipo_reserva as "individual" | "multipla" | "fechada_organizador" | "fechada_nao_organizador" | "grupo") || (item.fechado === 1 ? 'grupo' : 'individual'),
          const fechada = (reservationData.tipo_reserva === 'fechada_organizador' || reservationData.tipo_reserva === 'fechada_nao_organizador');

          // Cria FormData para compatibilidade com o backend legado
          const formData = new FormData();
          formData.append('method', 'onCriarReserva');
          formData.append('idagenda', String(reservationData.idagenda));
          formData.append('idcliente', String(reservationData.idcliente));
          formData.append('quantidade', String(reservationData.qtdpessoas));
          // Envia fechada como "1"/"0" para compatibilidade com backend legado
          formData.append('fechada', fechada ? '1' : '0');
          // Inclui explicitamente o tipo de reserva
          formData.append('tipo_reserva', reservationData.tipo_reserva);
          // Adiciona a data da corrida se fornecida
          if (reservationData.data_corrida) {
            formData.append('data_corrida', reservationData.data_corrida);
          }
          formData.append('sem_dados_pix', String(true)); // Indica que não há dados PIX

          // Faz a chamada à API
          // {|INICIO| 13/07/2026 às 11:41 - ENVIAR BEARER DO CLIENTE NA CRIACAO DE RESERVA}
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Authorization': montarValorAutorizacao(AUTH_SEED)
            },
            body: formData
          });
          // {|FIM| 13/07/2026 às 11:41 - ENVIAR BEARER DO CLIENTE NA CRIACAO DE RESERVA}
          
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('ReservationService.createReservation - Error response:', errorText);
            
            // Trata erro 403 Forbidden: limpa autenticação e força logout
            if (response.status === 403) {
              limparAutenticacao(true);
              throw new Error(this.getTranslation('errors.sessionExpired'));
            }
            
            throw new Error(`Erro ao criar reserva: ${response.statusText}`);
          }
          
          // Verifica o Content-Type antes de tentar fazer parse
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            const responseText = await response.text();
            console.error('ReservationService.createReservation - Invalid content type:', contentType);
            console.error('ReservationService.createReservation - Response text (first 500 chars):', responseText.substring(0, 500));
            throw new Error('A API retornou uma resposta inválida. Verifique se a URL da API está correta e se o servidor está funcionando adequadamente.');
          }
          
          const rawData = await parseJsonResponse(response);
          
          devLog('🔍 Dados recebidos da API de criação de reserva:', rawData);
          
          // A resposta vem aninhada: {status: 'success', data: '{"status":"Sucesso",...}'}
          // Precisamos fazer parse da string JSON no campo data
          let data;
          if (rawData.data && typeof rawData.data === 'string') {
            try {
              data = JSON.parse(rawData.data);
              devLog('🔍 Dados parseados do campo data:', data);
            } catch (parseError) {
              console.error('Erro ao fazer parse do campo data:', parseError);
              throw new Error('Resposta da API em formato inválido');
            }
          } else {
            data = rawData;
          }
          
          // Verifica se a resposta contém um erro
          if (data.status === 'Erro') {
            const msg: string = `${data.message ?? ''}`;
            // Fallback: backend informa duplicidade com número(s) (ID)
            /*
            const match = msg.match(/número\(s\)\s*\((\d+)\)/);
            if (match) {
              const existingId = Number(match[1]);
              const reservation: Reservation = {
                id: existingId,
                idagenda: reservationData.idagenda,
                idcliente: reservationData.idcliente,
                qtdpessoas: reservationData.qtdpessoas,
                // Não atribuir valorprepago em fallback de reserva existente
                tipo_reserva: reservationData.tipo_reserva,
                status_pagamento: 'pending',
                confirmado: 0,
                datareserva: new Date().toISOString(),
                horario_inicio: new Date().toISOString()
              };
              console.warn('♻️ Reserva já existente detectada; reutilizando idreserva da mensagem:', existingId);
              return reservation;
            }
            */
            throw new Error(msg);
          }
          
          // Verifica se a reserva foi criada com sucesso e tem idreserva
          if (data.status !== 'Sucesso' || !data.idreserva) {
            throw new Error('Falha ao criar reserva: ID da reserva não foi retornado');
          }
          
          // Agora a API retorna o idreserva real do banco de dados
          const reservation: Reservation = {
            id: Number(data.idreserva), // ID real da reserva do banco de dados
            idagenda: reservationData.idagenda,
            idcliente: reservationData.idcliente,
            qtdpessoas: reservationData.qtdpessoas,
            // Não atribuir valorprepago na criação. Mantemos semântica: pago somente após confirmação.
            tipo_reserva: reservationData.tipo_reserva,
            status_pagamento: 'pending',
            confirmado: 0,
            datareserva: new Date().toISOString(),
            horario_inicio: new Date().toISOString()
          };
          
          devLog('📋 Reserva mapeada:', reservation);
          
          return reservation;
        });
      }, {
        maxRetries: 2,
        delayMs: 1000,
        shouldRetry: (error, attempt) => {
          return attempt < 2 && isRetryableError(error);
        }
      });
    } catch (error) {
      console.error('Erro ao criar reserva:', error);
      throw error;
    }
  }

  static async getMyReservations(idcliente: number): Promise<Reservation[]> {
    try {
      // Constrói a URL da API para buscar as reservas do cliente
      const url = `${getApiBaseUrl()}/reserva_online/onMinhasReservas/?idcliente=${idcliente}`;
      
      // Prepara os headers com autenticação básica
      // {|INICIO| 13/07/2026 às 11:41 - ENVIAR BEARER DO CLIENTE AO LISTAR MINHAS RESERVAS}
      const headers = new Headers();
      headers.append('Authorization', montarValorAutorizacao(AUTH_SEED));
      // {|FIM| 13/07/2026 às 11:41 - ENVIAR BEARER DO CLIENTE AO LISTAR MINHAS RESERVAS}
      
      const requestOptions = {
        method: 'GET',
        headers: headers,
        redirect: 'follow' as RequestRedirect,
      };
      
      // Faz a chamada à API
      const response = await fetch(url, requestOptions);
      
      if (!response.ok) {
        if (response.status === 403) {
          limparAutenticacao(true);
          throw new Error(this.getTranslation('errors.sessionExpired'));
        }
        throw new Error(`Erro ao buscar reservas: ${response.statusText}`);
      }
      
      const data = await parseJsonResponse(response);
      
      // Verifica se a resposta contém um erro
      if (data.status === 'Erro') {
        throw new Error(`${data.message}`);
      }
      
      // Se a resposta tem status success, usa data.data, senão usa data diretamente
      const reservasData = data.status === 'success' ? data.data : data;
      
      // Se não houver reservas, retorna um array vazio
      if (!Array.isArray(reservasData)) {
        return [];
      }

      // Normalizador local de status de pagamento para alinhar com a UI
      const normalizePaymentStatus = (s: unknown): Reservation['status_pagamento'] => {
        const v = String(s ?? '').toLowerCase();
        switch (v) {
          case 'paid':
          case 'captured':
            return 'paid';
          case 'paidmanual':
            return 'paidManual';
          case 'pending':
            return 'pending';
          case 'processing':
            return 'processing';
          case 'failed':
          case 'not_authorized':
            return 'failed';
          case 'canceled':
          case 'cancelled':
            return 'canceled';
          case 'authorized':
            return 'authorized';
          case 'chargeback':
          case 'chargedback':
            return 'chargedback';
          case 'conta_assinada':
            return 'conta_assinada';
          default:
            return 'pending';
        }
      };
      
      // Mapeia os dados da resposta para o formato Reservation[]
      return reservasData.map((item: Record<string, unknown>) => {
        
        // Usar os novos campos do backend: valor_unitario e valor_total
        const valorUnitario = Number((item as Record<string, unknown>)?.['valor_unitario']) || 0;
        // Priorizar valor_total_raw (float) se disponível, senão tenta converter valor_total
        const valorTotalRaw = Number((item as Record<string, unknown>)?.['valor_total_raw']);
        // {|INICIO| 02/04/2026 às 10:17 - LINT: prefer-const}
        const valorTotal = !isNaN(valorTotalRaw) ? valorTotalRaw : Number((item as Record<string, unknown>)?.['valor_total']) || 0;
        // {|FIM| 02/04/2026 às 10:17 - LINT: prefer-const}
        
        const qtdPessoas = Number((item as Record<string, unknown>)?.['qtdpessoas']) || 1;
        const valorprepago = Number((item as Record<string, unknown>)?.['valorprepago']) || 0;
        
        // Valores agregados vindos do backend (ReservaRestService)
        // O backend já calcula a soma de taxas (processamento + reagendamento) e descontos (feminino + voucher)
        const valorDescontos = Number((item as Record<string, unknown>)?.['valor_descontos'] || 0);
        const valorTaxas = Number((item as Record<string, unknown>)?.['valor_taxas'] || 0);
        /* {|INICIO| 01/06/2026 às 21:35 - DIFERENÇA POR TROCA DE HORÁRIO: MAPEAR CAMPOS DO BACKEND PARA HABILITAR PAGAMENTO COMPLEMENTAR} */
        // Comentário (function-level): Campos opcionais enviados pelo backend para contemplar troca de horário com valor maior.
        const valorUnitarioAgenda = Number((item as Record<string, unknown>)?.['valor_unitario_agenda'] || 0);
        const valorTotalRawAgenda = Number((item as Record<string, unknown>)?.['valor_total_raw_agenda'] || 0);
        const valorDiferencaTrocaHorario = Number((item as Record<string, unknown>)?.['valor_diferenca_troca_horario'] || 0);
        /* {|FIM| 01/06/2026 às 21:35 - DIFERENÇA POR TROCA DE HORÁRIO: MAPEAR CAMPOS DO BACKEND PARA HABILITAR PAGAMENTO COMPLEMENTAR} */

        // O backend envia valor_total_raw como (qtd * unitario), sem taxas/descontos.
        // Precisamos compor o valor final real (Grand Total) adicionando taxas e subtraindo descontos.
        let valorReserva = valorTotal;
        if (valorReserva > 0) {
           valorReserva = valorReserva + valorTaxas - valorDescontos;
        } else if (valorUnitario > 0) {
           valorReserva = (valorUnitario * qtdPessoas) + valorTaxas - valorDescontos;
        }

        const rawStatus = (item as Record<string, unknown>)?.['status_pagamento'];
        let normalizedStatus = normalizePaymentStatus(rawStatus);
        // Se reserva estiver cancelada, refletir na situação de pagamento quando status vier indefinido/pendente
        if ((rawStatus === undefined || rawStatus === null || normalizedStatus === 'pending') && Number((item as Record<string, unknown>)?.['confirmado'] || 0) === -1) {
          normalizedStatus = 'canceled';
        }
       
        return {
           id: Number((item as Record<string, unknown>)?.['codigo'] || (item as Record<string, unknown>)?.['id']),
           idagenda: Number((item as Record<string, unknown>)?.['idagenda'] || 0),
           idcliente: Number(idcliente),
           qtdpessoas: Number((item as Record<string, unknown>)?.['qtdpessoas'] || (item as Record<string, unknown>)?.['qtd_pessoas'] || 1),
           valor_unitario: valorUnitario,
           valor_total: valorReserva,
           valor_descontos: valorDescontos,
           valor_taxas: valorTaxas,
           /* {|INICIO| 01/06/2026 às 21:35 - DIFERENÇA POR TROCA DE HORÁRIO: CAMPOS PARA UI} */
           valor_unitario_agenda: valorUnitarioAgenda,
           valor_total_raw_agenda: valorTotalRawAgenda,
           valor_diferenca_troca_horario: valorDiferencaTrocaHorario,
           /* {|FIM| 01/06/2026 às 21:35 - DIFERENÇA POR TROCA DE HORÁRIO: CAMPOS PARA UI} */
           valorprepago: valorprepago,
           payment_method: (item as Record<string, unknown>)?.['payment_method'] as string,
           motivo_recusa: (item as Record<string, unknown>)?.['motivo_recusa'] as string,
           tipo_reserva: ((item as Record<string, unknown>)?.['tipo_reserva'] as "individual" | "multipla" | "fechada_organizador" | "fechada_nao_organizador" | "grupo") || (((item as Record<string, unknown>)?.['fechado']) === 1 ? 'grupo' : 'individual'),
           status_pagamento: normalizedStatus,
           confirmado: Number((item as Record<string, unknown>)?.['confirmado'] || 0),
           fechado: Number((item as Record<string, unknown>)?.['fechado'] || 0),
           // {|INICIO| 29/03/2026 às 23:38 - PREFERIR data_reserva_datetime PARA EXIBIR HORA/MINUTO EM "RESERVADO EM"}
           datareserva: ((item as Record<string, unknown>)?.['data_reserva_datetime'] || (item as Record<string, unknown>)?.['data_reserva'] || (item as Record<string, unknown>)?.['datareserva'] || '') as string,
           // {|FIM| 29/03/2026 às 23:38 - PREFERIR data_reserva_datetime PARA EXIBIR HORA/MINUTO EM "RESERVADO EM"}
           horario_inicio: ((item as Record<string, unknown>)?.['horario_inicio'] || '') as string,
           horario_termino: ((item as Record<string, unknown>)?.['horario_termino'] || '') as string,
           data_corrida: ((item as Record<string, unknown>)?.['data_corrida'] || (item as Record<string, unknown>)?.['horario_inicio'] || '') as string,
           codigo: (item as Record<string, unknown>)?.['codigo'] as string,
           descricao: (item as Record<string, unknown>)?.['nome_item'] as string, // Campo que contém o nome do item
           participantes: []
         };
      });
    } catch (error) {
      console.error('Erro ao buscar reservas do cliente:', error);
      throw error;
    }
  }

  /**
   * Busca reserva EXISTENTE do mesmo cliente para a mesma agenda (confirmado <> -1),
   * evitando re-criação quando o cliente recarrega a página após um timeout/loading longo.
   * Usado como proteção anti-duplicata no frontend antes de disparar novo createReservation.
   */
  static async findExistingReservationByAgendaCliente(
    idagenda: number,
    idcliente: number
  ): Promise<Reservation | null> {
    try {
      if (!idcliente || !idagenda) return null;
      const reservas = await this.getMyReservations(idcliente);
      if (!Array.isArray(reservas) || reservas.length === 0) return null;
      const encontrada = reservas.find((r) => {
        const rIdAgenda = Number((r as any).idagenda ?? (r as any).fk_idagenda ?? 0);
        const confirmado = (r as any).confirmado;
        return rIdAgenda === Number(idagenda) && confirmado !== -1;
      });
      return encontrada ?? null;
    } catch (_erro) {
      return null;
    }
  }

  /**
   * Cancela uma reserva específica
   * @param reservationId - ID da reserva a ser cancelada
   * @returns Promise<void>
   */
  static async cancelReservation(reservationId: number): Promise<void> {
    try {
      // Constrói a URL da API para cancelar uma reserva (seguindo padrão do sistema legado)
      const url = `${getApiBaseUrl()}/reserva_online/onCancelarReserva/?codigo=${reservationId}`;
      
      // Prepara os headers com autenticação (seguindo padrão do sistema legado)
      // {|INICIO| 13/07/2026 às 11:41 - ENVIAR BEARER DO CLIENTE AO CANCELAR RESERVA}
      const headers = new Headers();
      headers.append('Authorization', montarValorAutorizacao(AUTH_SEED));
      // {|FIM| 13/07/2026 às 11:41 - ENVIAR BEARER DO CLIENTE AO CANCELAR RESERVA}
      
      const requestOptions = {
        method: 'POST',
        headers: headers,
        redirect: 'follow' as RequestRedirect
      };
      
      // Faz a chamada à API
      const response = await fetch(url, requestOptions);
      
      if (!response.ok) {
        if (response.status === 403) {
          limparAutenticacao(true);
          throw new Error(this.getTranslation('errors.sessionExpired'));
        }
        throw new Error(`Erro ao cancelar reserva: ${response.statusText}`);
      }
      
      const result = await parseJsonResponse(response);
      
      // Verifica se a resposta contém um erro (seguindo padrão do sistema legado)
      if (result.status === 'success') {
        if (result.data && result.data.status === 'Erro') {
          throw new Error(`${result.data.message || this.getTranslation('errors.cancellationProcessingError')}`);
        }
        // Sucesso - reserva cancelada
        return;
      } else {
        throw new Error(`${result.data?.message || result.message || this.getTranslation('errors.cancellationFailed')}`);
      }
      
    } catch (error) {
      console.error('Erro ao cancelar reserva:', error);
      throw error;
    }
  }

  static async getReservationPayments(reservationId: number) {
    try {
      // Constrói a URL da API para buscar os pagamentos de uma reserva
      const url = `${getApiBaseUrl()}/index.php?class=ReservaRestService&method=getPayments&id=${reservationId}`;
      
      // Faz a chamada à API
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar pagamentos: ${response.statusText}`);
      }
      
      const data = await parseJsonResponse(response);
      
      // Verifica se a resposta contém um erro
      if (data.status === 'Erro') {
        throw new Error(`${data.message}`);
      }
      
      return {
        total: Number(data.total || 0),
        paid: Number(data.paid || 0)
      };
    } catch (error) {
      console.error('Erro ao buscar pagamentos da reserva:', error);
      throw error;
    }
  }

  /**
   * Obtém os dados de um cliente pelo ID
   * @param clienteId ID do cliente a ser consultado
   * @returns Dados do cliente ou mensagem de erro
   */
  static async getCliente(clienteId: number): Promise<ClienteResponse> {
    try {
      // Usa a rota REST padronizada do serviço ReservaRestService
      const url = `${getApiBaseUrl()}/reserva_online/getCliente/?id=${clienteId}`;
      // {|INICIO| 22/07/2026 às 16:05 - DEBUG: EXIGIR BEARER REAL NO PERFIL E LIMPAR SESSAO INCONSISTENTE}
      const response = await fetch(url, { headers: ReservationService.getBearerAuthHeaders() });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          limparAutenticacao(true);
        }
        throw new Error(`Erro ao buscar dados do cliente: ${response.statusText}`);
      }
      // {|FIM| 22/07/2026 às 16:05 - DEBUG: EXIGIR BEARER REAL NO PERFIL E LIMPAR SESSAO INCONSISTENTE}

      const data = await parseJsonResponse(response);

      // Estrutura aninhada (Adianti wrapper): {status:'success', data:{status:'Sucesso', data:{...}}}
      if (data && data.status === 'success' && data.data && (data.data.status === 'Sucesso' || data.data.status === 'Successo')) {
        return { status: 'Sucesso', data: data.data.data as User };
      }

      // Estrutura direta: {status:'Sucesso', data:{...}}
      if (data && (data.status === 'Sucesso' || data.status === 'Successo') && data.data) {
        return { status: 'Sucesso', data: data.data as User };
      }

      if (data && data.status === 'Erro') {
        return { status: 'Erro', message: data.message };
      }

      // Fallback
      return { status: 'Sucesso', data: (data?.data ?? data) as User };
    } catch (error) {
      console.error('Erro ao buscar dados do cliente:', error);
      return { status: 'Erro', message: error instanceof Error ? error.message : this.getTranslation('errors.unknown') };
    }
  }

  // {|INICIO| 27/04/2026 às 00:20 - API: OBTER HTML SEGURO DO AVISO VERMELHO PARA O BANNER (RESERVA ONLINE)}
  // {|INICIO| 16/06/2026 às 21:22 - API: ENVIAR DATA SELECIONADA PARA RESOLVER AVISO ESPECÍFICO NO BACKEND}
  // Comentário (function-level): Carrega HTML do aviso vermelho via ReservaRestService.getTextoAvisoReservaOnline e informa se o conteúdo veio de uma mensagem específica do dia.
  static async getTextoAvisoReservaOnlineHtml(idConfiguracao: number = 1, dataSelecionada?: string): Promise<{ html: string; temAvisoDia: boolean }> {
    try {
      const idConfiguracaoValido = Number.isFinite(Number(idConfiguracao)) && Number(idConfiguracao) > 0 ? Math.floor(Number(idConfiguracao)) : 1;
      const params = new URLSearchParams({ id: String(idConfiguracaoValido) });
      if (typeof dataSelecionada === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dataSelecionada)) {
        params.set('data', dataSelecionada);
      }
      const url = `${getApiBaseUrl()}/reserva_online/getTextoAvisoReservaOnline/?${params.toString()}`;

      // {|INICIO| 22/07/2026 às 16:05 - DEBUG: TRATAR AVISO PUBLICO SEM DEPENDER DE BEARER DO CLIENTE}
      const response = await fetch(url, { method: 'GET', headers: ReservationService.getBasicAuthHeaders() });
      // {|FIM| 22/07/2026 às 16:05 - DEBUG: TRATAR AVISO PUBLICO SEM DEPENDER DE BEARER DO CLIENTE}
      if (!response.ok) {
        throw new Error(`Erro ao buscar aviso da reserva online: ${response.status} ${response.statusText}`);
      }

      // {|INICIO| 18/06/2026 às 11:24 - API: CONSUMIR CONTRATO DIRETO DO BACKEND PARA O AVISO DA RESERVA ONLINE}
      /**
       * Function-level comment: Normaliza a flag booleana do contrato direto do endpoint sem depender de payloads legados aninhados.
       */
      const resolverTemAvisoDia = (valor: unknown): boolean => {
        if (typeof valor === 'boolean') return valor;
        if (typeof valor === 'number') return valor > 0;
        if (typeof valor === 'string') {
          return ['1', 'true', 's', 'sim', 'y', 'yes'].includes(valor.trim().toLowerCase());
        }
        return false;
      };

      const result = await parseJsonResponse(response) as Record<string, unknown>;

      if (result.status === 'error') {
        const mensagemErro = typeof result.message === 'string' ? result.message : this.getTranslation('errors.processingError');
        throw new Error(mensagemErro);
      }

      const dadosAviso = (result.data && typeof result.data === 'object')
        ? (result.data as Record<string, unknown>)
        : null;

      return {
        html: typeof dadosAviso?.html === 'string' ? dadosAviso.html : '',
        temAvisoDia: resolverTemAvisoDia(dadosAviso?.tem_aviso_dia ?? dadosAviso?.temAvisoDia),
      };
      // {|FIM| 18/06/2026 às 11:24 - API: CONSUMIR CONTRATO DIRETO DO BACKEND PARA O AVISO DA RESERVA ONLINE}
    } catch (error) {
      devLog('Erro ao obter aviso da reserva online:', error);
      return { html: '', temAvisoDia: false };
    }
  }
  // {|FIM| 16/06/2026 às 21:22 - API: ENVIAR DATA SELECIONADA PARA RESOLVER AVISO ESPECÍFICO NO BACKEND}
  // {|FIM| 27/04/2026 às 00:20 - API: OBTER HTML SEGURO DO AVISO VERMELHO PARA O BANNER (RESERVA ONLINE)}

  /**
   * Helper to get translation in static context
   */
  static getTranslation(key: string, params?: Record<string, string>): string {
    const lang = (typeof localStorage !== 'undefined' ? localStorage.getItem('language') : 'pt') || 'pt';
    const json = lang === 'en' ? enJson : (lang === 'es' ? esJson : ptJson);
    
    // Simple deep access
    const val = key.split('.').reduce((o: any, k) => o?.[k], json) || key;
    
    if (params && typeof val === 'string') {
        return Object.entries(params).reduce((str, [k, v]) => str.replace(`{${k}}`, v), val);
    }
    return typeof val === 'string' ? val : key;
  }

  /**
   * Atualiza os dados de um cliente
   * @param clienteData Dados do cliente a serem atualizados
   * @returns Resposta da API com status da operação
   */
  static async updateCliente(clienteData: Partial<User>): Promise<ClienteResponse> {
    try {
      const isBrazil = !clienteData.codigo_ibge_pais || clienteData.codigo_ibge_pais === 1058;

      // Conditional formatting
      if (clienteData.cpf_cnpj) {
        if (isBrazil) {
           clienteData.cpf_cnpj = clienteData.cpf_cnpj.replace(/\D/g, '');
        } else {
           clienteData.cpf_cnpj = clienteData.cpf_cnpj.trim().toUpperCase();
        }
      }

      // Validação de campos obrigatórios
      if (!clienteData.id) {
        return { status: 'Erro', message: this.getTranslation('errors.clientIdRequired') };
      }
      if (!clienteData.nome) {
        return { status: 'Erro', message: this.getTranslation('errors.nameRequired') };
      }

      // Document Validation
      const docLabel = isBrazil ? this.getTranslation('auth.cpf') : this.getTranslation('auth.passport');

      if (!clienteData.cpf_cnpj) {
        return { status: 'Erro', message: this.getTranslation('auth.documentRequired', { doc: docLabel }) };
      }

      if (!clienteData.email) {
        return { status: 'Erro', message: this.getTranslation('errors.emailRequired') };
      }
      if (!clienteData.data_nascimento) {
        return { status: 'Erro', message: this.getTranslation('errors.birthDateRequired') };
      }
      
      if (isBrazil) {
        if (!this.validarCPF(clienteData.cpf_cnpj)) {
          return { status: 'Erro', message: this.getTranslation('auth.invalidDocument', { doc: docLabel }) };
        }
      } else {
        // Para passaporte, apenas verifica se não está vazio (outras validações já foram feitas no frontend)
        if (!clienteData.cpf_cnpj || clienteData.cpf_cnpj.trim().length < 5) {
             return { status: 'Erro', message: this.getTranslation('auth.invalidDocument', { doc: docLabel }) };
        }
      }

      // Formatação da data de nascimento para ISO (YYYY-MM-DD) aceito pelo PHP DateTime
      const dataISO = this.formatDateISO(String(clienteData.data_nascimento));
      clienteData.data_nascimento = dataISO;

      // Verifica se é menor de idade
      const menorDeIdade = this.verificaIdade(dataISO);

      // Validações específicas para menor de idade
      if (menorDeIdade && (!clienteData.responsavel || !clienteData.cpf_responsavel || !clienteData.telefone_responsavel)) {
        return { status: 'Erro', message: this.getTranslation('errors.minorGuardianRequired') };
      }

      if (menorDeIdade && (clienteData.cpf_cnpj === clienteData.cpf_responsavel || 
          clienteData.nome === clienteData.responsavel || clienteData.telefone_celular === clienteData.telefone_responsavel)) {
        return { status: 'Erro', message: this.getTranslation('errors.guardianDataSameAsClient') };
      }

      // {|INICIO| 08/04/2026 às 14:30 - PERFIL: VALIDAR CEP NO updateCliente QUANDO PAÍS FOR BRASIL}
      if (isBrazil) {
        const cepInformado = String(clienteData.cep ?? '').trim();
        const cepSomenteDigitos = cepInformado.replace(/\D/g, '');
        const cepFoiPreenchido = cepInformado !== '';

        if (!cepFoiPreenchido) {
          const temDadosEndereco =
            Boolean(String(clienteData.endereco ?? '').trim()) ||
            Boolean(String(clienteData.bairro ?? '').trim()) ||
            Boolean(String(clienteData.estado ?? '').trim()) ||
            Boolean(String(clienteData.cidade ?? '').trim()) ||
            Boolean(String(clienteData.numero ?? '').trim());

          if (temDadosEndereco) {
            return { status: 'Erro', message: this.getTranslation('errors.cepRequired') };
          }
        } else {
          if (cepSomenteDigitos.length !== 8 || cepSomenteDigitos === '00000000') {
            return { status: 'Erro', message: this.getTranslation('errors.invalidCep') };
          }
        }
      }
      // {|FIM| 08/04/2026 às 14:30 - PERFIL: VALIDAR CEP NO updateCliente QUANDO PAÍS FOR BRASIL}

      // {|INICIO| 15/05/2026 às 11:05 - PERFIL: endereço internacional sem normalização BR (CEP/UF/IBGE) }
      // Prepara os dados para envio (mapear chaves esperadas pelo backend)
      // {|INICIO| 22/07/2026 às 14:44 - ETAPA 5: ENVIAR PAYLOAD EXPLICITO NA ATUALIZACAO DO PERFIL}
      const requestData: Record<string, unknown> = {
        nome: clienteData.nome,
        email: clienteData.email,
        cpf_cnpj: isBrazil
          ? (clienteData.cpf_cnpj?.replace(/\D/g, '') ?? '')
          : (clienteData.cpf_cnpj?.trim().toUpperCase() ?? ''),
        data_nascimento: clienteData.data_nascimento,
        telefone_residencial: clienteData.telefone_residencial,
        telefone_comercial: clienteData.telefone_comercial,
        telefone_celular: clienteData.telefone_celular,
        endereco: clienteData.endereco,
        numero: clienteData.numero,
        complemento: clienteData.complemento,
        bairro: clienteData.bairro,
        cidade: clienteData.cidade,
        estado: clienteData.estado,
        sexo: clienteData.sexo,
        codigo_ibge_pais: clienteData.codigo_ibge_pais,
        tipo_pessoa: clienteData.tipo_pessoa,
        responsavel: clienteData.responsavel,
        cpf_responsavel: clienteData.cpf_responsavel,
        telefone_responsavel: clienteData.telefone_responsavel,
        // Backend usa coluna 'rg_ie' para RG/IE
        rg_ie: (clienteData as Record<string, unknown>)?.['rg_ie'] ?? (clienteData as Record<string, unknown>)?.['rg'] ?? undefined,
        // Mapeia UF/cidade para os nomes esperados pelo Tcliente (apenas Brasil: códigos)
        codigo_uf: (isBrazil && clienteData.estado) ? String(clienteData.estado) : undefined,
        codigo_ibge_cidade: (isBrazil && clienteData.cidade) ? String(clienteData.cidade) : undefined,
        // CEP: Brasil em dígitos; estrangeiro mantém texto livre (código postal)
        cep: (() => {
          if (!clienteData.cep) return undefined;
          const rawCep = String(clienteData.cep).trim();
          if (!rawCep) return undefined;
          return isBrazil ? rawCep.replace(/\D/g, '') : rawCep;
        })()
      };
      // {|FIM| 22/07/2026 às 14:44 - ETAPA 5: ENVIAR PAYLOAD EXPLICITO NA ATUALIZACAO DO PERFIL}
      // {|FIM| 15/05/2026 às 11:05 - PERFIL: endereço internacional sem normalização BR (CEP/UF/IBGE) }

      // Constrói FormData para compatibilidade total com services PHP (usa $_POST)
      const formData = new FormData();
      Object.entries(requestData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });

      // O backend ainda usa 'telefone' em algumas validações; enviar alias se disponível
      if (clienteData.telefone_celular) {
        formData.append('telefone', String(clienteData.telefone_celular));
      }
      // Constrói a URL da API para atualizar os dados do cliente (compatível com legado)
      const url = `${getApiBaseUrl()}/reserva_online/onSetCliente/?id=${clienteData.id}`;

      // Configura cabeçalhos (não define Content-Type para FormData)
      const headers = new Headers();
      // {|INICIO| 22/07/2026 às 09:13 - PERFIL: PRIORIZAR BEARER DO CLIENTE NA ATUALIZACAO DO CADASTRO}
      headers.append('Authorization', montarValorAutorizacao(AUTH_SEED));
      // {|FIM| 22/07/2026 às 09:13 - PERFIL: PRIORIZAR BEARER DO CLIENTE NA ATUALIZACAO DO CADASTRO}
      headers.append('Accept', 'application/json');

      // Faz a chamada à API enviando FormData
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: formData,
        redirect: 'follow'
      });

      if (!response.ok) {
        throw new Error(`Erro ao atualizar dados do cliente: ${response.statusText}`);
      }

      const data = await parseJsonResponse(response);

      // Normaliza possíveis estruturas de resposta (Adianti wrapper ou direta)
      if (data && data.status === 'success') {
        const inner = data.data || {};
        if (inner.status === 'Erro') {
          return { status: 'Erro', message: inner.message || this.getTranslation('errors.processingError') };
        }
        return { status: 'Sucesso', message: inner.message || this.getTranslation('messages.clientDataUpdatedSuccess') };
      }

      if (data && data.status === 'Erro') {
        return { status: 'Erro', message: data.message };
      }

      return {
        status: 'Sucesso',
        message: (data?.message || this.getTranslation('messages.clientDataUpdatedSuccess'))
      };
    } catch (error) {
      console.error('Erro ao atualizar dados do cliente:', error);
      return { status: 'Erro', message: error instanceof Error ? error.message : 'An unknown error occurred' };
    }
  }

  /**
   * Obtém a lista de origens disponíveis
   * @returns Lista de origens ou mensagem de erro
   */
  static async getOrigens(): Promise<OrigemResponse> {
    try {
      const url = `${getApiBaseUrl()}/reserva_online/getOrigens/`;
      const response = await fetch(url, { headers: ReservationService.getAuthHeaders() });

      if (!response.ok) {
        throw new Error(`Erro ao buscar origens: ${response.statusText}`);
      }

      const data = await parseJsonResponse(response);

      // Estrutura aninhada: {status:'success', data:{status:'Sucesso', data:[...]}}
      if (data && data.status === 'success' && data.data && data.data.status === 'Sucesso' && Array.isArray(data.data.data)) {
        return { status: 'Sucesso', data: data.data.data };
      }

      // Estrutura direta: {status:'Sucesso', data:[...]}
      if (data && data.status === 'Sucesso' && Array.isArray(data.data)) {
        return { status: 'Sucesso', data: data.data };
      }

      if (data && data.status === 'Erro') {
        return { status: 'Erro', message: data.message };
      }

      // Fallback seguro
      const arr = Array.isArray(data?.data) ? data.data : [];
      return { status: 'Sucesso', data: arr };
    } catch (error) {
      console.error('Erro ao buscar origens:', error);
      return { status: 'Erro', message: error instanceof Error ? error.message : this.getTranslation('errors.unknown') };
    }
  }

  /**
   * Obtém a lista de estados disponíveis
   * @returns Lista de estados ou mensagem de erro
   */
  static async getEstados(): Promise<EstadoResponse> {
    try {
      const url = `${getApiBaseUrl()}/reserva_online/getEstados/`;
      const response = await fetch(url, { headers: ReservationService.getAuthHeaders() });

      if (!response.ok) {
        throw new Error(`Erro ao buscar estados: ${response.statusText}`);
      }

      const data = await parseJsonResponse(response);

      // Estrutura aninhada: {status:'success', data:{status:'Sucesso', data:[...]}}
      if (data && data.status === 'success' && data.data && data.data.status === 'Sucesso' && Array.isArray(data.data.data)) {
        return { status: 'Sucesso', data: data.data.data };
      }

      // Estrutura direta: {status:'Sucesso', data:[...]}
      if (data && data.status === 'Sucesso' && Array.isArray(data.data)) {
        return { status: 'Sucesso', data: data.data };
      }

      if (data && data.status === 'Erro') {
        return { status: 'Erro', message: data.message };
      }

      const arr = Array.isArray(data?.data) ? data.data : [];
      return { status: 'Sucesso', data: arr };
    } catch (error) {
      console.error('Erro ao buscar estados:', error);
      return { status: 'Erro', message: error instanceof Error ? error.message : this.getTranslation('errors.unknown') };
    }
  }

  /**
   * Obtém a lista de cidades de um estado
   * @param codigoUF Código UF do estado
   * @returns Lista de cidades ou mensagem de erro
   */
  static async getCidades(codigoUF: number): Promise<CidadeResponse> {
    try {
      if (!codigoUF) {
        return { status: 'Erro', message: this.getTranslation('errors.ufCodeRequired') };
      }

      const url = `${getApiBaseUrl()}/reserva_online/getCidades/?codigo_uf=${codigoUF}`;
      const requestData = { codigo_uf: codigoUF };

      const response = await fetch(url, {
        method: 'POST',
        headers: ReservationService.getAuthHeaders(),
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar cidades: ${response.statusText}`);
      }

      const data = await parseJsonResponse(response);

      // Estrutura aninhada: {status:'success', data:{status:'Sucesso', data:[...]}}
      if (data && data.status === 'success' && data.data && data.data.status === 'Sucesso' && Array.isArray(data.data.data)) {
        return { status: 'Sucesso', data: data.data.data };
      }

      // Estrutura direta: {status:'Sucesso', data:[...]}
      if (data && data.status === 'Sucesso' && Array.isArray(data.data)) {
        return { status: 'Sucesso', data: data.data };
      }

      if (data && data.status === 'Erro') {
        return { status: 'Erro', message: data.message };
      }

      const arr = Array.isArray(data?.data) ? data.data : [];
      return { status: 'Sucesso', data: arr };
    } catch (error) {
      console.error('Erro ao buscar cidades:', error);
      return { status: 'Erro', message: error instanceof Error ? error.message : this.getTranslation('errors.unknown') };
    }
  }

  /**
   * Consulta os pontos de um cliente
   * @param idcliente ID do cliente
   * @returns Resposta da API com os pontos do cliente
   */
  static async consultarPontos(idcliente: number): Promise<{ pontos?: number; status?: string; message?: string } | null> {
    const requestOptions = {
      method: 'POST',
      // {|INICIO| 23/07/2026 às 09:48 - AUTH: PRIORIZAR BEARER NA CONSULTA DE PONTOS DO CLIENTE}
      headers: this.getAuthHeaders(),
      // {|FIM| 23/07/2026 às 09:48 - AUTH: PRIORIZAR BEARER NA CONSULTA DE PONTOS DO CLIENTE}
      redirect: 'follow' as RequestRedirect
    };

    try {
      const url = `${getApiBaseUrl()}/reserva_online/onConsultaPontosCliente/?idcliente=${idcliente}`;
      const response = await fetch(url, requestOptions);
      const result = await response.json();
      return (result && typeof result === 'object') ? (result as { pontos?: number; status?: string; message?: string }) : null;
    } catch (error) {
      console.error('Erro ao consultar pontos:', error);
      throw new Error(this.getTranslation('errors.pointsFetchError'));
    }
  }

  static async cadastrarUsuario(data: {
    nome: string;
    email: string;
    cpf_cnpj: string;
    data_nascimento: string;
    telefone_celular: string;
    sexo?: string;
    codigo_ibge_pais?: number;
    // {|INICIO| 12/03/2026 às 21:40 - CADASTRO: ACEITAR NACIONALIDADE (TIPO_PESSOA) NO SERVIÇO}
    tipo_pessoa?: string;
    // {|FIM| 12/03/2026 às 21:40 - CADASTRO: ACEITAR NACIONALIDADE (TIPO_PESSOA) NO SERVIÇO}
  }): Promise<Record<string, unknown>> {
    try {
      // Create FormData object
      const formData = new FormData();
      formData.append('nome', data.nome);
      formData.append('cpf_cnpj', data.cpf_cnpj);
      formData.append('email', data.email);
      formData.append('data_nascimento', data.data_nascimento);
      formData.append('telefone_celular', data.telefone_celular);
      formData.append('sexo', data.sexo || ''); // Envia o sexo selecionado
      formData.append('codigo_ibge_pais', (data.codigo_ibge_pais || 1058).toString()); // Código padrão Brasil
      // {|INICIO| 12/03/2026 às 21:40 - CADASTRO: ENVIAR NACIONALIDADE (TIPO_PESSOA) PARA BACKEND}
      if (data.tipo_pessoa) {
        formData.append('tipo_pessoa', data.tipo_pessoa);
      }
      // {|FIM| 12/03/2026 às 21:40 - CADASTRO: ENVIAR NACIONALIDADE (TIPO_PESSOA) PARA BACKEND}

      // Get authorization headers (without Content-Type for FormData)
      const headers = new Headers();
      headers.append("Authorization", "Basic " + AUTH_SEED);
      // Don't set Content-Type for FormData - browser will set it automatically with boundary

      // Configure request options
      const requestOptions: RequestInit = {
        method: 'POST',
        headers: headers,
        body: formData,
        redirect: 'follow'
      };

      // Make API call
      const url_full = getApiBaseUrl() + "/reserva_online/onCriarUsuario/";
      const response = await fetch(url_full, requestOptions);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();

      return (result && typeof result === 'object') ? (result as Record<string, unknown>) : {};
    } catch (error) {
      console.error('Erro no cadastrarUsuario:', error);
      const errorMsg = error instanceof Error ? error.message : this.getTranslation('errors.unknown');
      throw new Error(this.getTranslation('errors.executionFailed', { error: errorMsg }));
    }
  }

  /**
   * Troca a senha do cliente
   * @param idcliente ID do cliente
   * @param senhaAtual Senha atual do cliente
   * @param novaSenha Nova senha do cliente
   * @param confirmarSenha Confirmação da nova senha
   * @returns Resposta da API com status da operação
   */
  static async trocarSenha(idcliente: number, senhaAtual: string, novaSenha: string, confirmarSenha?: string): Promise<TrocarSenhaResponse> {
    try {
      // {|INICIO| 22/07/2026 às 16:43 - TROCA DE SENHA: EXIGIR BEARER E REMOVER DEPENDENCIA DE IDCLIENTE DO FRONT}
      const headers = this.getBearerAuthHeaders();
      // {|INICIO| 22/07/2026 às 16:50 - TROCA DE SENHA: REMOVER CONTENT-TYPE JSON AO ENVIAR FORMDATA}
      headers.delete('Content-Type');
      // Histórico preservado: o helper Bearer padrão também define `Content-Type: application/json`.
      // Para `FormData`, o navegador precisa montar o multipart boundary automaticamente.
      // headers.append('Content-Type', 'application/json');
      // {|FIM| 22/07/2026 às 16:50 - TROCA DE SENHA: REMOVER CONTENT-TYPE JSON AO ENVIAR FORMDATA}
      const tokenAutenticacao = headers.get('Authorization');

      console.log('TrocarSenha - Dados:', {
        idcliente,
        possuiTokenAutenticacao: Boolean(tokenAutenticacao),
        senhaAtualLength: senhaAtual.length,
        novaSenhaLength: novaSenha.length
      });

      if (!tokenAutenticacao) {
        return {
          status: 'error',
          message: this.getTranslation('errors.sessionExpired')
        };
      }

      const formData = new FormData();
      formData.append("senhaAtual", senhaAtual);
      formData.append("novaSenha", novaSenha);
      if (typeof confirmarSenha === 'string' && confirmarSenha.trim() !== '') {
        formData.append("confirmarSenha", confirmarSenha.trim());
      }

      // Histórico preservado: o frontend enviava `idcliente` na URL e no corpo.
      // O backend agora resolve a identidade pelo token Bearer autenticado.
      // formData.append("idcliente", String(idcliente));
      // const url = `${getApiBaseUrl()}/reserva_online/onTrocarSenha/?idcliente=${idcliente}`;
      const url = `${getApiBaseUrl()}/reserva_online/onTrocarSenha/`;

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: formData,
        redirect: 'follow'
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          limparAutenticacao(true);
          return {
            status: 'error',
            message: this.getTranslation('errors.sessionExpired')
          };
        }

        const corpoErroBruto = await response.text();
        const corpoErroNormalizado = corpoErroBruto.replace(/\s+/g, ' ').trim();
        throw new Error(corpoErroNormalizado || `HTTP error! status: ${response.status}`);
      }

      const result = await parseJsonResponse(response);
      
      if (result.status === 'success' && result.data.status === 'Sucesso') {
        return {
          status: 'success',
          message: result.data.message || result.message || this.getTranslation('messages.passwordChangedSuccess')
          
        };
      } else {
        return {
          status: 'error',
          message: result.data.message || result.message || this.getTranslation('errors.passwordChangeError')
        };
      }
      // {|FIM| 22/07/2026 às 16:43 - TROCA DE SENHA: EXIGIR BEARER E REMOVER DEPENDENCIA DE IDCLIENTE DO FRONT}
    } catch (error) {
      console.error('Erro ao trocar senha:', error);
      if (error instanceof Error && (
        error.message.includes('Sessão expirada') ||
        error.message.includes('Session expired') ||
        error.message.includes('Authorization error')
      )) {
        return {
          status: 'error',
          message: this.getTranslation('errors.sessionExpired')
        };
      }
      return {
        status: 'error',
        message: this.getTranslation('errors.passwordChangeExecutionError', { 
          error: error instanceof Error ? error.message : this.getTranslation('errors.unknown') 
        })
      };
    }
  }

  static async getAnoExercicio(): Promise<{
    ano_exercicio: number;
    data_inicio_agenda_online: string | null;
    meses_lib_agenda_online: number | null;
  }> {
    try {
      /**
       * Obtém o ano de exercício sempre pelo backend real em /kgvt.
       * Mantemos apenas fallbacks equivalentes ao mesmo backend para evitar
       * dependência do espelho local removido.
       */
      const base = getApiBaseUrl() || '';
      const idcliente = typeof window !== 'undefined' ? (window.localStorage?.getItem('login_id') || '') : '';
      const qsBase = new URLSearchParams({ id: '1' });
      if (idcliente) qsBase.set('idcliente', String(idcliente));
      const qs = qsBase.toString();
      // {|INICIO| 13/07/2026 às 14:10 - RESTAURAR FALLBACKS DE AMBIENTE PARA ANO EXERCICIO SEM ESPELHO LOCAL}
      const candidates = Array.from(new Set([
        `${base}/reserva_online/getAnoExercicio/?${qs}`,
        `/kgvt/reserva_online/getAnoExercicio/?${qs}`,
        `/kgv/reserva_online/getAnoExercicio/?${qs}`,
      ].filter(Boolean)));
      // {|FIM| 13/07/2026 às 13:51 - REMOVER FALLBACKS DO ESPELHO LOCAL DO ANO EXERCICIO}

      // Função de requisição para tryMultipleEndpoints com validação de JSON
      const requestFn = async (url: string) => {
        // Suporte a AbortSignal.timeout quando disponível, sem uso de any explícito
        const signalTimeout: AbortSignal | undefined = (typeof AbortSignal !== 'undefined' && 'timeout' in (AbortSignal as unknown as Record<string, unknown>))
          ? ((AbortSignal as unknown as { timeout: (ms: number) => AbortSignal }).timeout(15000))
          : undefined;

        const response = await fetch(url, {
          method: 'GET',
          headers: this.getAuthHeaders(),
          signal: signalTimeout
        });

        if (!response.ok) {
          throw new Error(`Erro ao obter dados: ${response.status} ${response.statusText}`);
        }

        // Usa parseJsonResponse para tratar respostas HTML e erros comuns
        const dados = await parseJsonResponse(response);
        return { dados, endpointUsado: url };
      };

      // Tenta múltiplos endpoints até obter uma resposta válida
      // {|INICIO| 02/04/2026 às 10:17 - LINT: prefer-const E VARIÁVEL NÃO UTILIZADA}
      const { dados, endpointUsado: _endpointUsado } = await tryMultipleEndpoints(candidates, requestFn);
      // {|FIM| 02/04/2026 às 10:17 - LINT: prefer-const E VARIÁVEL NÃO UTILIZADA}
      void _endpointUsado;

      // console.info('getAnoExercicio: endpoint utilizado', endpointUsado);

      const payload: Record<string, unknown> = (dados && typeof dados === 'object') ? (((dados as Record<string, unknown>)?.['data']) ?? (dados as Record<string, unknown>)) as Record<string, unknown> : {};
      let dadosExercicio: Record<string, unknown> | undefined;
      if (payload && (payload as Record<string, unknown>)?.['ano_exercicio']) {
        dadosExercicio = payload;
      } else if (Array.isArray(payload) && payload.length > 0 && (payload[0] as Record<string, unknown>)?.['ano_exercicio']) {
        dadosExercicio = payload[0] as Record<string, unknown>;
      } else {
        throw new Error('Ano de exercício não encontrado.');
      }

      // Retorna o objeto completo, com todos os campos da resposta
      return {
        ano_exercicio: Number((dadosExercicio as Record<string, unknown>)?.['ano_exercicio']) || new Date().getFullYear(),
        data_inicio_agenda_online: (((dadosExercicio as Record<string, unknown>)?.['data_inicio_agenda_online'] ?? null) as (string | null)),
        meses_lib_agenda_online: (typeof (dadosExercicio as Record<string, unknown>)?.['meses_lib_agenda_online'] !== 'undefined' && (dadosExercicio as Record<string, unknown>)?.['meses_lib_agenda_online'] !== null)
          ? Number((dadosExercicio as Record<string, unknown>)?.['meses_lib_agenda_online'] as number)
          : null
      };
    } catch (error) {
      console.error('Erro ao obter ano de exercício:', error);

      const currentYear = new Date().getFullYear();
      return {
        ano_exercicio: currentYear,
        data_inicio_agenda_online: null,
        meses_lib_agenda_online: null
      };
    }
  }

  // Nova função para buscar dados da agenda por idagenda
  static async getAgendaById(idagenda: number): Promise<Agenda | null> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/reserva_online/getAgenda/?idagenda=${idagenda}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        console.warn(`Erro ao buscar agenda ${idagenda}: ${response.status}`);
        return null;
      }

      const result = await parseJsonResponse(response);
      
      if (result.status === 'success' && result.data) {
        const agendaData = Array.isArray(result.data) ? result.data[0] : result.data;
        
        if (agendaData) {
          return {
            idagenda: Number(agendaData.idagenda || idagenda),
            nomeitem: agendaData.nomeitem || 'Serviço não informado',
            horainicio: agendaData.horainicio || '',
            horatermino: agendaData.horatermino || '',
            valorliquido: Number(agendaData.valorliquido || 0),
            vagas: Number(agendaData.vagas || 0),
            vagas_totais: Number(agendaData.vagas_totais || 0),
            status: agendaData.status,
            fechado: Boolean(agendaData.fechado),
            parcela_cartao_venda_online: this.normalizeBooleanFlag(agendaData.parcela_cartao_venda_online),
            parcelamento_venda_online_efetivo: this.normalizeBooleanFlag(agendaData.parcelamento_venda_online_efetivo),
            /* {|INICIO| 18/06/2026 às 16:39 - NORMALIZAR LIMITE EXCLUSIVO DE QUANTIDADE NA BUSCA UNITARIA DA AGENDA} */
            permite_somente_quantidade: (typeof agendaData.permite_somente_quantidade !== 'undefined')
              ? (typeof agendaData.permite_somente_quantidade === 'string'
                  ? parseInt(agendaData.permite_somente_quantidade)
                  : Number(agendaData.permite_somente_quantidade))
              : undefined,
            quantidade_maxima_por_cpf_disponivel: (typeof agendaData.quantidade_maxima_por_cpf_disponivel !== 'undefined')
              ? (typeof agendaData.quantidade_maxima_por_cpf_disponivel === 'string'
                  ? parseInt(agendaData.quantidade_maxima_por_cpf_disponivel)
                  : Number(agendaData.quantidade_maxima_por_cpf_disponivel))
              : undefined,
            /* {|FIM| 18/06/2026 às 16:39 - NORMALIZAR LIMITE EXCLUSIVO DE QUANTIDADE NA BUSCA UNITARIA DA AGENDA} */
            // Comentário (function-level): Normalização de min_vagas_bat_fechada para Agenda
            // Quando presente, converte string/number de forma segura; caso contrário, mantém undefined.
            min_vagas_bat_fechada: (typeof agendaData.min_vagas_bat_fechada !== 'undefined')
              ? (typeof agendaData.min_vagas_bat_fechada === 'string'
                  ? parseInt(agendaData.min_vagas_bat_fechada)
                  : Number(agendaData.min_vagas_bat_fechada))
              : undefined
          };
        }
      }
      
      return null;
    } catch (error) {
      console.warn('Erro ao buscar dados da agenda:', error);
      return null;
    }
  }

  // Nova função para obter dias sem agenda (dias desabilitados)
  static async obterDiasSemAgenda(dataSelecionada: Date): Promise<number[]> {
    try {
      const _date = dataSelecionada ? new Date(dataSelecionada) : new Date();
      const mesAno = `${(_date.getMonth() + 1).toString().padStart(2, '0')}/${_date.getFullYear()}`;
      
      const response = await fetch(`${getApiBaseUrl()}/reserva_online/onAgendamentos_sem_vaga/?mesano=${mesAno}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await parseJsonResponse(response);
      
      if (result.status === 'success' && result.data && Array.isArray(result.data)) {
        // Mapeia os objetos retornados pela API para extrair os dias
        const diasSemAgenda = result.data.map((objeto: Record<string, unknown>) => {
          const date = new Date(objeto.horario_inicio + 'T00:00:00');
          return date.getDate();
        });

        // Adicionar dias anteriores à data atual (apenas dias passados, não o dia atual)
        const dataAtual = new Date();
        const diaAtual = dataAtual.getDate();
        const mesAtual = dataAtual.getMonth();
        const anoAtual = dataAtual.getFullYear();

        if (_date.getMonth() === mesAtual && _date.getFullYear() === anoAtual) {
          // Gera uma lista de dias anteriores ao dia atual (excluindo o dia atual)
          const diasAnteriores = diaAtual > 1 ? Array.from({ length: diaAtual - 1 }, (_, i) => i + 1) : [];
          // Combina os dias anteriores com os retornados pela API
          const dias = [...new Set([...diasSemAgenda, ...diasAnteriores])];
          return dias;
        } else {
          // Apenas os dias retornados pela API
          return diasSemAgenda;
        }
      } else {
        console.warn("Nenhum dado encontrado para dias sem agenda.");
        throw new Error('API não retornou dados válidos');
      }
    } catch (error) {
      console.warn('API onAgendamentos_sem_vaga não disponível, usando dados simulados:', error);
      
      // Dados simulados que seguem a mesma lógica do código original
      const _date = dataSelecionada ? new Date(dataSelecionada) : new Date();
      const dataAtual = new Date();
      const diaAtual = dataAtual.getDate();
      const mesAtual = dataAtual.getMonth();
      const anoAtual = dataAtual.getFullYear();
      
      // Simula alguns dias sem agenda (domingos e dias específicos)
      const diasSemAgenda: number[] = [];
      const daysInMonth = new Date(_date.getFullYear(), _date.getMonth() + 1, 0).getDate();
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(_date.getFullYear(), _date.getMonth(), day);
        // Desabilita domingos (0) e alguns dias específicos para demonstração
        if (date.getDay() === 0 || [5, 12, 19, 26].includes(day)) {
          diasSemAgenda.push(day);
        }
      }

      if (_date.getMonth() === mesAtual && _date.getFullYear() === anoAtual) {
        // Gera uma lista de dias anteriores ao dia atual (excluindo o dia atual)
        const diasAnteriores = diaAtual > 1 ? Array.from({ length: diaAtual - 1 }, (_, i) => i + 1) : [];
        // Combina os dias anteriores com os simulados
        const dias = [...new Set([...diasSemAgenda, ...diasAnteriores])];
        return dias;
      } else {
        // Apenas os dias simulados
        return diasSemAgenda;
      }
    }
  }
}
